// dependences
import { useMobile } from 'hooks/handleSize';
import { useLanguage } from 'hooks/i18n';
import { useSuccessCases, matchFeaturedClients } from 'hooks/useSuccessCases';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router';

// components
import DesktopMenu from './desktopMenu/presentational';
import MobileMenu from './mobileMenu/presentational';

const Navbar = () => {
  const [t, i18n] = useTranslation('global');
  const [showLanguages, setShowLanguages] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [language, setLanguage] = useLanguage();
  const [advertisingView, setAdvertisingView] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMobile();

  const v1 = window.location.pathname.includes('1') ||  window.location.pathname.includes('2');
  const v2 = window.location.pathname.includes('3') ||  window.location.pathname.includes('4');

  useEffect(() => {
    if(!isMobile) {
      setShowMobileMenu(false);
    }
  }, [isMobile]);

  // nav menu: items with `menu` open a mega menu (desktop) / accordion (mobile)
  // success stories (Academy articles) for the "Clientes" menu: only downloaded once the menu is opened
  const [clientsMenuOpened, setClientsMenuOpened] = useState(false);
  // the Clientes page already downloads the articles: reuse them (shared cache, no extra request)
  const onArticlesPage = /\/cases\/?$/.test(location.pathname);
  const { cases: successCases, loading: casesLoading } = useSuccessCases(language, { skip: !clientsMenuOpened && !onArticlesPage });
  const onMenuOpen = (key) => {
    if (key === 'clients') setClientsMenuOpened(true);
  };
  const featuredClients = useMemo(() => matchFeaturedClients(successCases), [successCases]);

  const navItems = useMemo(() => {
    const link = (key, path, icon) => ({
      key,
      icon,
      path: `/${language}${path}`,
      title: t(`header.menu.${key}.title`),
      desc: t(`header.menu.${key}.desc`),
    });
    return [
      {
        path: `/${language}/platform`,
        title: t('header.platform'),
        key: 'platform',
        menu: [
          {
            title: t('header.menu.howItWorks'),
            wide: true,
            links: [
              link('listen', '/platform#datacapture', 'listen'),
              link('understand', '/platform#dataanalytic', 'understand'),
              link('impact', '/platform#impact', 'impact'),
              link('actStep', '/platform#acction', 'actStep'),
            ],
          },
          {
            title: t('header.menu.platform'),
            links: [
              link('sources', '/sources', 'sources'),
              link('howWeWork', '/how-we-work', 'howWeWork'),
            ],
          },
        ],
      },
      {
        path: `/${language}/solutions/retention`,
        title: t('header.menu.solutions'),
        key: 'solutions',
        menu: [
          {
            title: t('header.menu.byImpact'),
            links: [
              link('solRetention', '/solutions/retention', 'solRetention'),
              link('solCrossSell', '/solutions/cross-sell', 'solCrossSell'),
              link('solRecommendation', '/solutions/recommendation', 'solRecommendation'),
              link('solEfficiency', '/solutions/efficiency', 'solEfficiency'),
              link('solDecisions', '/solutions/decisions', 'solDecisions'),
            ],
          },
          {
            title: t('header.menu.byIndustry'),
            links: [
              link('indBanking', '/industries/banking', 'indBanking'),
              link('indInsurance', '/industries/insurance', 'indInsurance'),
              link('indAirlines', '/industries/airlines', 'indAirlines'),
              link('indRetail', '/industries/retail', 'indRetail'),
              link('indTelco', '/industries/telco', 'indTelco'),
            ],
          },
        ],
      },
      {
        path: `/${language}/agents`,
        title: t('header.menu.agentsMenu'),
        key: 'agents',
        menu: [
          {
            title: t('header.menu.smileAgents'),
            links: [
              link('agListen', '/agents#agent-listen', 'agListen'),
              link('agAnalysis', '/agents#agent-analysis', 'agAnalysis'),
              link('agCases', '/agents#agent-cases', 'agCases'),
              link('agAlerts', '/agents#agent-alerts', 'agAlerts'),
            ],
          },
          {
            title: t('header.menu.forYourAi'),
            links: [
              link('agCustom', '/agents#custom-agents', 'agCustom'),
              link('agConnect', '/agents#connect-ai', 'agConnect'),
            ],
          },
        ],
      },
      {
        path: `/${language}/cases`,
        title: t('header.menu.clients'),
        key: 'clients',
        menu: [
          {
            title: t('header.menu.featuredCases'),
            grid: true,
            // each featured client opens its success story; without one yet, the clients page
            // only clients that have a success story in the current language
            // while the stories download: skeleton placeholders instead of an empty column
            links: casesLoading && !successCases.length
              ? Array.from({ length: 6 }, (_, i) => ({ key: `skeleton-${i}`, skeleton: true }))
              : featuredClients.filter((client) => client.caseId).map((client) => ({
                key: client.name,
                icon: 'case',
                path: `/${language}/post/${client.caseId}`,
                title: client.name,
                desc: '',
              })),
          },
          {
            title: t('header.menu.clients'),
            links: [link('allClients', '/cases', 'allClients')],
          },
        ],
      },
      {
        path: `/${language}/academy`,
        title: t('header.menu.resources'),
        key: 'resources',
        menu: [
          {
            title: t('header.menu.resources'),
            links: [
              link('recCalculator', '/calculator', 'recCalculator'),
              link('recAcademy', '/academy', 'recAcademy'),
              link('recSecurity', '/security', 'recSecurity'),
              link('status', '/status', 'status'),
            ],
          },
          {
            title: t('header.menu.companySupport'),
            links: [
              link('help', '/help', 'help'),
              link('aboutUs', '/about', 'aboutUs'),
              link('jobs', '/about#join', 'jobs'),
              link('partners', '/about#partners', 'partners'),
            ],
          },
        ],
      },
    ];
  }, [language, t, featuredClients, casesLoading, successCases.length]);

  const navAdvertisingItems = useMemo(() => [
    {
      title: t('header.platform'),
      key: 'platform',
      path: '#platform'
    },
    {
      title: t('header.clients'),
      key: 'clients',
      path: '#clients',
    },
    {
      title: t('header.contact'),
      path: '#contact',
    },
  ], [t])

  useEffect(() => {
    if(window.location.pathname.includes('free-demo-cx')) {
      setAdvertisingView(true);
    } else {
      setAdvertisingView(false);
    }
  }, [location]);


  const languagesList = ['es', 'en', 'pt'];

  // change Language
  const changeLanguage = (language) => {
    const split = location.pathname.split('/');
    split[1] = language;
    history.replace(split.join('/') + location.search)
    setLanguage(language);
    setShowLanguages(false);
    setShowMobileMenu(false);
  };

  const showLanguagesFn = () => {
    setShowLanguages(!showLanguages);
  };

  const sendDemoEvent = () => {
    window.ga('send', 'event', 'button', 'click_button', 'free_demo')
  }

  // mobile functions
  return (
    <>
      <DesktopMenu
        changeLanguage={changeLanguage}
        navItems={location.pathname.includes('free-demo-cx') ? navAdvertisingItems : navItems}
        onMenuOpen={onMenuOpen}
        languagesList={languagesList}
        showLanguages={showLanguages}
        showLanguagesFn={showLanguagesFn}
        i18n={i18n}
        language={language}
        pathname={location.pathname}
        sendDemoEvent={sendDemoEvent}
        advertisingView={advertisingView}
        version2={!v1}
      />
      <MobileMenu
        changeLanguage={changeLanguage}
        navItems={location.pathname.includes('free-demo-cx') ? navAdvertisingItems : navItems}
        onMenuOpen={onMenuOpen}
        languagesList={languagesList}
        showLanguages={showLanguages}
        showLanguagesFn={showLanguagesFn}
        i18n={i18n}
        setShowMobileMenu={setShowMobileMenu}
        showMobileMenu={showMobileMenu}
        language={language}
        sendDemoEvent={sendDemoEvent}
        advertisingView={advertisingView}
        version2={v2}
      />

    </>
  );
};

export default Navbar;
